/**
 * Quiz collection editor page.
 * Hosts collection settings, question authoring, topic weighting, ownership, and bulk import tools.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Switch,
  Spin,
  Popconfirm,
  Select,
  Collapse,
  Typography,
  Tooltip,
  Image,
  Avatar,
  Slider,
  Tabs,
  Checkbox,
  Upload,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HolderOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  TagsOutlined,
  CheckOutlined,
  TeamOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
  ImportOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  ClockCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PagesWrapper from '../../PagesWrapper';
import ImageDropZone from '../../components/ImageDropZone';
import { AccessBanner, OwnershipManager } from '../../components/Ownership';
import { useOwnership } from '../../hooks/useOwnership';
import quizCollectionsService from '../../services/quizCollectionsService';
import levelsOfDifficultyService from '../../services/levelsOfDifficultyService';
import { getFileUrl } from '../../utils/urlUtils';
import { formatDurationFromMilliseconds } from '../../utils/timeUtils';
import styles from './Quiz.module.css';
import BulkImportModal from './BulkImportModal';
import QuestionAnalyticsModal from './QuestionAnalyticsModal';

const { TextArea } = Input;
const { Text, Title } = Typography;

/**
 * Normalize publish/unpublish validation responses from the API.
 * Handles collection-level unpublish, question-level unpublish, and explicit publish rejection messages.
 *
 * @param {object} validationResult - Validation payload returned by the backend.
 * @param {Function} setCollection - State setter for collection data.
 * @param {Function} setQuestions - State setter for question data.
 * @param {Function} t - i18n translation function.
 * @returns {void}
 */
const handleAutoUnpublishResult = (validationResult, setCollection, setQuestions, t) => {
  // Handle question publish rejection (tried to publish but failed validation)
  if (validationResult?.questionPublishWasRejected) {
    const errorList = validationResult.questionValidationErrors?.join(', ') || 'Question is invalid';
    Modal.warning({
      title: t('quiz.questionCannotPublish.title', 'Cannot Publish Question'),
      content: t(
        'quiz.questionCannotPublish.message',
        'This question cannot be published. Please fix these issues first: {{errors}}',
        { errors: errorList }
      ),
    });
    // Don't update state - the question remains a draft
    return;
  }
  
  // Handle question-level auto-unpublish (was published, edited, became invalid)
  if (validationResult?.questionWasAutoUnpublished) {
    const errorList = validationResult.questionValidationErrors?.join(', ') || 'Question is now invalid';
    Modal.warning({
      title: t('quiz.questionAutoUnpublished.title', 'Question Unpublished'),
      content: t(
        'quiz.questionAutoUnpublished.message',
        'This question was automatically unpublished because it is no longer valid: {{errors}}. Please fix the issues and publish again.',
        { errors: errorList }
      ),
    });
    // The question's isPublished state should be updated from the API response
  }
  
  // Handle collection-level auto-unpublish
  if (validationResult?.wasAutoUnpublished) {
    const errorList = validationResult.errors?.join(', ') || 'Collection is now invalid';
    Modal.warning({
      title: t('quiz.autoUnpublished.title', 'Quiz Collection Unpublished'),
      content: t(
        'quiz.autoUnpublished.message',
        'Your quiz collection was automatically unpublished because it is no longer valid: {{errors}}. Please fix the issues and publish again.',
        { errors: errorList }
      ),
    });
    setCollection(prev => ({ ...prev, isPublished: false }));
  }
};

/**
 * Sortable summary card for an authored quiz question.
 * Presents metadata, publish state, weights, and key edit actions inside the editor list.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} Sortable question card.
 */
const SortableQuestionCard = ({ question, onEdit, onDelete, onTogglePublish, onManageWeights, onOpenAnalytics, stats, topicsCount }) => {
  const { t } = useTranslation();
  const hasWeights = question.weights?.length > 0;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const correctChoices = question.choices?.filter(c => c.isCorrect) || [];
  const totalChoices = question.choices?.length || 0;
  const isMultiSelect = correctChoices.length > 1;

  const handlePublishClick = (e) => {
    e.stopPropagation();
    onTogglePublish(question);
  };

  const handlePdfClick = (e) => {
    e.stopPropagation();
    if (question.pdfUrl) {
      window.open(getFileUrl(question.pdfUrl), '_blank');
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.questionCard}
    >
      {/* Top bar: drag handle, code, tags, actions */}
      <div className={styles.qcHeader}>
        <div className={styles.qcHeaderLeft}>
          <HolderOutlined
            className={styles.dragHandle}
            {...attributes}
            {...listeners}
          />
          <span className={styles.qcCode}>{question.code}</span>
          {isMultiSelect && (
            <Tag color="geekblue" className={styles.qcTag}>{t('quiz.multiSelect', 'Multi-select')}</Tag>
          )}
          {question.pdfUrl && (
            <Tooltip title={t('quiz.viewPdf', 'View PDF')}>
              <Tag
                color="red"
                className={styles.qcTagClickable}
                icon={<FilePdfOutlined />}
                onClick={handlePdfClick}
              >
                PDF
              </Tag>
            </Tooltip>
          )}
        </div>
        <div className={styles.qcHeaderRight}>
          <Tooltip title={question.isPublished ? t('quiz.clickToUnpublish', 'Click to unpublish') : t('quiz.clickToPublish', 'Click to publish')}>
            <Button
              size="small"
              type={question.isPublished ? 'primary' : 'default'}
              icon={question.isPublished ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              onClick={handlePublishClick}
              style={{
                background: question.isPublished ? '#52c41a' : undefined,
                borderColor: question.isPublished ? '#52c41a' : undefined,
                fontSize: 12,
              }}
            >
              {question.isPublished ? t('quiz.published', 'Published') : t('quiz.draft', 'Draft')}
            </Button>
          </Tooltip>
          {topicsCount > 0 && (
            <Tooltip title={t('quiz.manageWeights', 'Manage Topic Weights')}>
              <Button 
                type="text" 
                size="small" 
                icon={<ApartmentOutlined />} 
                onClick={() => onManageWeights(question)}
                className={hasWeights ? styles.weightsButtonActive : ''}
              />
            </Tooltip>
          )}
          <Tooltip title={t('quiz.editQuestion', 'Edit')}>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(question)} />
          </Tooltip>
          <Popconfirm
            title={t('quiz.deleteQuestionConfirm', 'Delete this question?')}
            onConfirm={() => onDelete(question.id)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
          >
            <Tooltip title={t('quiz.deleteQuestion', 'Delete')}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </div>
      </div>

      {/* Body: image + text */}
      <div className={styles.qcBody}>
        {question.imageUrl && (
          <div className={styles.qcImageWrap} onClick={(e) => e.stopPropagation()}>
            <Image
              src={getFileUrl(question.imageUrl)}
              alt=""
              className={styles.qcImage}
              preview={{ mask: null }}
              width={300}
              height={300}
              style={{ objectFit: 'cover', borderRadius: 4 }}
            />
          </div>
        )}
        <div className={styles.qcText}>
          <div
            className={styles.qcBodyText}
            dangerouslySetInnerHTML={{ __html: question.body }}
          />
        </div>
      </div>

      {/* Footer: metadata row + choices summary */}
      <div className={styles.qcFooter}>
        <div className={styles.qcMetaRow}>
          {question.levelOfDifficultyName && (
            <Tag color={question.levelOfDifficultyColor || 'blue'} style={{ margin: 0, borderRadius: 20 }}>{question.levelOfDifficultyName}</Tag>
          )}
          {question.topic && (
            <span className={styles.qcMetaChip}>
              <span className={styles.qcMetaChipLabel}>{t('quiz.topicText', 'Topic')}:</span>
              <span className={styles.qcMetaChipValue}>{question.topic}</span>
            </span>
          )}
          {question.subtopic && (
            <span className={styles.qcMetaChip}>
              <span className={styles.qcMetaChipLabel}>{t('quiz.subtopic', 'Subtopic')}:</span>
              <span className={styles.qcMetaChipValue}>{question.subtopic}</span>
            </span>
          )}
          {question.law && (
            <span className={styles.qcMetaChip}>
              <span className={styles.qcMetaChipLabel}>{t('quiz.law', 'Law')}:</span>
              <span className={styles.qcMetaChipValue}>{question.law}</span>
            </span>
          )}
          {question.version && (
            <span className={styles.qcMetaChip}>
              <span className={styles.qcMetaChipLabel}>{t('quiz.version', 'Version')}:</span>
              <span className={styles.qcMetaChipValue}>{question.version}</span>
            </span>
          )}
        </div>

        {/* Topic Weights Row */}
        {hasWeights && (
          <div className={styles.qcWeightsRow}>
            <ApartmentOutlined className={styles.qcWeightsIcon} />
            <div className={styles.qcWeightsList}>
              {[...question.weights]
                .filter(w => w.weight > 0)
                .sort((a, b) => b.weight - a.weight)
                .map(w => (
                  <div key={w.topicId} className={styles.qcWeightItem}>
                    <span className={styles.qcWeightName}>{w.topicName}</span>
                    <span 
                      className={styles.qcWeightValue}
                      style={{
                        backgroundColor: w.weight >= 0.75 ? '#f6ffed' : 
                                        w.weight >= 0.5 ? '#e6f4ff' : 
                                        w.weight >= 0.25 ? '#fff7e6' : '#f5f5f5',
                        color: w.weight >= 0.75 ? '#389e0d' : 
                               w.weight >= 0.5 ? '#1677ff' : 
                               w.weight >= 0.25 ? '#d48806' : '#8c8c8c',
                        borderColor: w.weight >= 0.75 ? '#b7eb8f' : 
                                     w.weight >= 0.5 ? '#91caff' : 
                                     w.weight >= 0.25 ? '#ffd591' : '#d9d9d9'
                      }}
                    >
                      {Math.round(w.weight * 100)}%
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className={styles.qcChoicesRow}>
          {/* Stats Bar */}
          <div className={styles.qcChoicesStats}>
            <span className={styles.qcStatItem}>
              <span className={styles.qcStatNumber}>{totalChoices}</span>
              <span className={styles.qcStatLabel}>{t('quiz.choices', 'choices')}</span>
            </span>
            <span className={styles.qcStatDivider}>·</span>
            <span className={`${styles.qcStatItem} ${styles.qcStatCorrect}`}>
              <CheckCircleOutlined />
              <span className={styles.qcStatNumber}>{correctChoices.length}</span>
              <span className={styles.qcStatLabel}>{t('quiz.correct', 'correct')}</span>
            </span>

            {/* Question-level analytics: success rate + median play time.
                Clickable group opens a deep analytics modal. */}
            <Tooltip
              title={
                stats && stats.totalAnswered > 0
                  ? t('quiz.questionAnalytics.viewDetails', 'View question analytics')
                  : t('quiz.questionAnalytics.noDataYet', 'No play data yet — click for details')
              }
            >
              <div
                className={styles.qcAnalyticsGroup}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAnalytics(question);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenAnalytics(question);
                  }
                }}
              >
                {stats && stats.totalAnswered > 0 ? (
                  <>
                    <span
                      className={`${styles.qcAnalyticsPill} ${styles.qcAnalyticsPillCount}`}
                    >
                      <span className={styles.qcAnalyticsHashIcon} aria-hidden="true">#</span>
                      {stats.totalAnswered}
                    </span>
                    <span
                      className={`${styles.qcAnalyticsPill} ${styles.qcAnalyticsPillSuccess}`}
                    >
                      <CheckCircleOutlined />
                      {Math.round(stats.accuracyPercent ?? 0)}%
                    </span>
                    <span
                      className={`${styles.qcAnalyticsPill} ${styles.qcAnalyticsPillTime}`}
                    >
                      <ClockCircleOutlined />
                      {stats.medianDurationMs != null
                        ? formatDurationFromMilliseconds(stats.medianDurationMs)
                        : '—'}
                    </span>
                  </>
                ) : (
                  <span className={`${styles.qcAnalyticsPill} ${styles.qcAnalyticsPillEmpty}`}>
                    <BarChartOutlined />
                    {t('quiz.questionAnalytics.noStats', 'No stats yet')}
                  </span>
                )}
                <RightOutlined className={styles.qcAnalyticsCaret} />
              </div>
            </Tooltip>
          </div>

          {/* Correct Answers List */}
          {correctChoices.length > 0 && (
            <div className={styles.qcCorrectList}>
              {correctChoices.slice(0, 3).map((c, idx) => (
                <div key={c.id} className={styles.qcCorrectItem}>
                  <span className={styles.qcCorrectIndex}>
                    <CheckOutlined />
                    <span>{idx + 1}</span>
                  </span>
                  {c.imageUrl && (
                    <div className={styles.qcCorrectItemImgWrap} onClick={(e) => e.stopPropagation()}>
                      <Image
                        src={getFileUrl(c.imageUrl)}
                        alt=""
                        width={200}
                        height={200}
                        style={{ objectFit: 'contain', borderRadius: 4 }}
                        preview={{ mask: null }}
                      />
                    </div>
                  )}
                  <span className={styles.qcCorrectItemText}>
                    {c.text || (c.imageUrl ? t('quiz.imageAnswer', 'Image answer') : '—')}
                  </span>
                </div>
              ))}
              {correctChoices.length > 3 && (
                <div className={styles.qcCorrectMore}>
                  +{correctChoices.length - 3} {t('quiz.more', 'more')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Sortable choice row rendered inside the question editing modal.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} Sortable choice item.
 */
const SortableChoiceItem = ({ choice, index, onEdit, onDelete, onToggleCorrect }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: choice.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.choiceItem} ${choice.isCorrect ? styles.correct : ''}`}
    >
      <HolderOutlined className={styles.dragHandle} {...attributes} {...listeners} />
      <Checkbox 
        checked={choice.isCorrect} 
        onChange={() => onToggleCorrect(choice)}
      >
        <span>{choice.text || (choice.imageUrl ? t('quiz.imageOnly', '(Image only)') : t('quiz.noContent', '(No content)'))}</span>
      </Checkbox>
      {choice.imageUrl && (
        <Image
          src={getFileUrl(choice.imageUrl)}
          alt=""
          width={48}
          height={48}
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      )}
      <div className={styles.choiceActions}>
        <Tooltip title={t('quiz.editChoice', 'Edit')}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(choice)} />
        </Tooltip>
        <Popconfirm
          title={t('quiz.deleteChoiceConfirm', 'Delete this choice?')}
          onConfirm={() => onDelete(choice.id)}
          okText={t('common.yes', 'Yes')}
          cancelText={t('common.no', 'No')}
        >
          <Tooltip title={t('quiz.deleteChoice', 'Delete')}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      </div>
    </div>
  );
};

/**
 * Form-compatible publish state toggle used in editor forms.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} Publish state button.
 */
const PublishButton = ({ value, onChange }) => {
  const { t } = useTranslation();
  
  return (
    <Button
      type={value ? 'primary' : 'default'}
      icon={value ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
      onClick={() => onChange?.(!value)}
      style={{
        minWidth: 120,
        background: value ? '#52c41a' : undefined,
        borderColor: value ? '#52c41a' : undefined,
      }}
    >
      {value ? t('quiz.published', 'Published') : t('quiz.draft', 'Draft')}
    </Button>
  );
};

/**
 * Form-compatible correct-answer toggle for choice editing.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} Correct-answer toggle control.
 */
const CorrectAnswerToggle = ({ value, onChange }) => {
  const { t } = useTranslation();
  
  return (
    <div 
      className={`${styles.correctAnswerToggle} ${value ? styles.correctAnswerActive : ''}`}
      onClick={() => onChange?.(!value)}
    >
      <div className={styles.correctAnswerToggleContent}>
        <div className={styles.correctAnswerIcon}>
          {value ? (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 24 }} />
          )}
        </div>
        <div className={styles.correctAnswerTextWrap}>
          <span className={styles.correctAnswerTitle}>
            {t('quiz.isCorrectAnswer', 'This is a correct answer')}
          </span>
          <span className={styles.correctAnswerHint}>
            {t('quiz.correctAnswerHint', 'Mark this choice as one of the correct answers')}
          </span>
        </div>
      </div>
      <Switch 
        checked={value} 
        onChange={(checked, e) => {
          e.stopPropagation();
          onChange?.(checked);
        }}
      />
    </div>
  );
};

/**
 * Modal for editing how strongly a question maps to collection topics.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} Topic weights modal.
 */
const WeightsModal = ({ open, question, topics, onClose, onSave, saving }) => {
  const { t } = useTranslation();
  const [weights, setWeights] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize weights from question data when modal opens
  useEffect(() => {
    if (open && question) {
      const initialWeights = {};
      topics.forEach(topic => {
        const existingWeight = question.weights?.find(w => w.topicId === topic.id);
        initialWeights[topic.id] = existingWeight ? existingWeight.weight : 0;
      });
      setWeights(initialWeights);
      setHasChanges(false);
    }
  }, [open, question, topics]);

  const handleWeightChange = (topicId, value) => {
    setWeights(prev => ({ ...prev, [topicId]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    const weightsArray = Object.entries(weights)
      .filter(([_, weight]) => weight > 0)
      .map(([topicId, weight]) => ({
        topicId: parseInt(topicId),
        weight: weight
      }));
    onSave(question.id, weightsArray);
  };

  const handleReset = () => {
    const resetWeights = {};
    topics.forEach(topic => {
      resetWeights[topic.id] = 0;
    });
    setWeights(resetWeights);
    setHasChanges(true);
  };

  const getWeightLabel = (weight) => {
    if (weight === 0) return t('quiz.weights.notRelevant', 'Not relevant');
    if (weight <= 0.25) return t('quiz.weights.slightlyRelevant', 'Slightly relevant');
    if (weight <= 0.5) return t('quiz.weights.moderatelyRelevant', 'Moderately relevant');
    if (weight <= 0.75) return t('quiz.weights.relevant', 'Relevant');
    return t('quiz.weights.highlyRelevant', 'Highly relevant');
  };

  const getWeightColor = (weight) => {
    if (weight === 0) return '#d9d9d9';
    if (weight <= 0.25) return '#fadb14';
    if (weight <= 0.5) return '#faad14';
    if (weight <= 0.75) return '#1890ff';
    return '#52c41a';
  };

  const configuredCount = Object.values(weights).filter(w => w > 0).length;

  return (
    <Modal
      title={
        <div className={styles.weightsModalTitle}>
          <ApartmentOutlined className={styles.weightsModalIcon} />
          <div className={styles.weightsModalTitleText}>
            <span>{t('quiz.weights.title', 'Topic Weights')}</span>
            <span className={styles.weightsModalSubtitle}>
              {question?.code} - {t('quiz.weights.subtitle', 'Configure how this question relates to collection topics')}
            </span>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={640}
      className={styles.weightsModal}
      footer={[
        <Button key="reset" onClick={handleReset} disabled={saving}>
          {t('quiz.weights.resetAll', 'Reset All')}
        </Button>,
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel', 'Cancel')}
        </Button>,
        <Button 
          key="save" 
          type="primary" 
          onClick={handleSave} 
          loading={saving}
          disabled={!hasChanges}
        >
          {t('common.save', 'Save')}
        </Button>,
      ]}
    >
      <div className={styles.weightsModalContent}>
        {/* Info Banner */}
        <div className={styles.weightsInfoBanner}>
          <InfoCircleOutlined className={styles.weightsInfoIcon} />
          <div className={styles.weightsInfoText}>
            <Text strong>{t('quiz.weights.whatAreWeights', 'What are topic weights?')}</Text>
            <Text type="secondary">
              {t('quiz.weights.explanation', 'Topic weights indicate how relevant this question is to each topic. A weight of 0 means not relevant, while 1 means fully relevant. Questions can be filtered by topic during quiz play based on these weights.')}
            </Text>
          </div>
        </div>

        {/* Topics List */}
        {topics.length === 0 ? (
          <div className={styles.weightsEmptyState}>
            <TagsOutlined className={styles.weightsEmptyIcon} />
            <Text type="secondary">
              {t('quiz.weights.noTopics', 'No topics defined in this collection. Add topics first to configure weights.')}
            </Text>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className={styles.weightsSummary}>
              <div className={styles.weightsSummaryItem}>
                <span className={styles.weightsSummaryValue}>{topics.length}</span>
                <span className={styles.weightsSummaryLabel}>{t('quiz.weights.totalTopics', 'Total Topics')}</span>
              </div>
              <div className={styles.weightsSummaryDivider} />
              <div className={styles.weightsSummaryItem}>
                <span className={styles.weightsSummaryValue} style={{ color: '#52c41a' }}>{configuredCount}</span>
                <span className={styles.weightsSummaryLabel}>{t('quiz.weights.configured', 'Configured')}</span>
              </div>
              <div className={styles.weightsSummaryDivider} />
              <div className={styles.weightsSummaryItem}>
                <span className={styles.weightsSummaryValue} style={{ color: '#d9d9d9' }}>{topics.length - configuredCount}</span>
                <span className={styles.weightsSummaryLabel}>{t('quiz.weights.unconfigured', 'Not Set')}</span>
              </div>
            </div>

            {/* Weight Sliders */}
            <div className={styles.weightsSliderList}>
              {topics.map(topic => {
                const weight = weights[topic.id] || 0;
                const weightColor = getWeightColor(weight);
                
                return (
                  <div key={topic.id} className={styles.weightSliderItem}>
                    <div className={styles.weightSliderHeader}>
                      <div className={styles.weightSliderTopicInfo}>
                        <TagsOutlined style={{ color: '#1890ff' }} />
                        <Text strong className={styles.weightSliderTopicName}>{topic.name}</Text>
                      </div>
                      <div className={styles.weightSliderValueInfo}>
                        <Tag 
                          color={weight > 0 ? 'blue' : 'default'} 
                          style={{ margin: 0, minWidth: 48, textAlign: 'center' }}
                        >
                          {Math.round(weight * 100)}%
                        </Tag>
                        <Text 
                          type="secondary" 
                          className={styles.weightSliderLabel}
                          style={{ color: weightColor }}
                        >
                          {getWeightLabel(weight)}
                        </Text>
                      </div>
                    </div>
                    <div className={styles.weightSliderTrack}>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        value={weight}
                        onChange={(value) => handleWeightChange(topic.id, value)}
                        tooltip={{ 
                          formatter: (value) => `${Math.round(value * 100)}%`
                        }}
                        trackStyle={{ backgroundColor: weightColor }}
                        handleStyle={{ borderColor: weightColor }}
                        marks={{
                          0: '',
                          0.25: '',
                          0.5: '',
                          0.75: '',
                          1: ''
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

/**
 * Full authoring experience for a single quiz collection.
 * Includes collection details, topics, ownership, question editing, and publishing workflows.
 *
 * @returns {JSX.Element} Quiz collection editor page.
 */
const QuizCollectionEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [detailsForm] = Form.useForm();
  const [questionForm] = Form.useForm();
  const [choiceForm] = Form.useForm();
  const [topicForm] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [collection, setCollection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [levelsOfDifficulty, setLevelsOfDifficulty] = useState([]);

  // Modal states
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [choiceModalVisible, setChoiceModalVisible] = useState(false);
  const [topicModalVisible, setTopicModalVisible] = useState(false);
  const [ownershipModalVisible, setOwnershipModalVisible] = useState(false);
  const [weightsModalVisible, setWeightsModalVisible] = useState(false);
  const [bulkImportModalVisible, setBulkImportModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingChoice, setEditingChoice] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [weightsQuestion, setWeightsQuestion] = useState(null);
  const [savingWeights, setSavingWeights] = useState(false);
  const [currentQuestionChoices, setCurrentQuestionChoices] = useState([]);

  // Image states for forms
  const [questionImage, setQuestionImage] = useState(null);
  const [questionPdf, setQuestionPdf] = useState(null);
  const [clearQuestionImage, setClearQuestionImage] = useState(false);
  const [clearQuestionPdf, setClearQuestionPdf] = useState(false);
  const [choiceImage, setChoiceImage] = useState(null);
  const [clearChoiceImage, setClearChoiceImage] = useState(false);

  // Per-question stats (median time + accuracy) keyed by question id.
  // Sourced from the existing /statistics endpoint; refreshed silently
  // alongside the main collection fetch.
  const [questionStatsMap, setQuestionStatsMap] = useState({});

  // Question-level analytics modal state
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [analyticsQuestion, setAnalyticsQuestion] = useState(null);

  // Ownership management
  const {
    ownershipInfo,
    loading: ownershipLoading,
    canEdit,
    canDelete,
    canManageOwnership,
    shareWith,
    revoke,
    getAvailableUsers,
    availableUsers,
    loadingAvailableUsers,
  } = useOwnership('QuizCollection', parseInt(id));

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch collection data
  const fetchCollection = useCallback(async () => {
    setLoading(true);
    try {
      const [collectionData, levels] = await Promise.all([
        quizCollectionsService.getQuizCollection(id),
        levelsOfDifficultyService.getLevels({ pageSize: 100 }),
      ]);
      
      setCollection(collectionData);
      setQuestions(collectionData.questions || []);
      setTopics(collectionData.topics || []);
      setLevelsOfDifficulty(levels.items || []);
      
      detailsForm.setFieldsValue({
        name: collectionData.name,
      });
    } catch (error) {
      message.error(t('quiz.fetchError', 'Failed to load quiz collection'));
      navigate('/quiz-collections');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t, detailsForm]);

  /**
   * Pull aggregate statistics and reduce to a per-question lookup map.
   * Failures are non-fatal: the editor still loads, the stats pills just stay empty.
   */
  const fetchQuestionStats = useCallback(async () => {
    try {
      const stats = await quizCollectionsService.getQuizStatistics(id);
      const map = {};
      (stats?.questionStats || []).forEach((q) => {
        if (q?.questionId != null) map[q.questionId] = q;
      });
      setQuestionStatsMap(map);
    } catch (e) {
      // Silent: stats are a progressive enhancement.
    }
  }, [id]);

  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  useEffect(() => {
    fetchQuestionStats();
  }, [fetchQuestionStats]);

  // Save collection name
  const handleSaveDetails = async () => {
    try {
      const values = await detailsForm.validateFields();
      setSaving(true);
      
      const result = await quizCollectionsService.updateQuizCollection(id, values);
      setCollection(prev => ({ ...prev, ...values }));
      message.success(t('quiz.savedSuccess', 'Changes saved successfully'));
      
      if (result.validation) {
        handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error(t('quiz.saveError', 'Failed to save changes'));
    } finally {
      setSaving(false);
    }
  };

  // Toggle publish
  const handleTogglePublish = async () => {
    try {
      setPublishing(true);
      if (collection.isPublished) {
        await quizCollectionsService.unpublishQuizCollection(id);
        setCollection(prev => ({ ...prev, isPublished: false }));
        message.success(t('quiz.unpublishSuccess', 'Quiz collection unpublished'));
      } else {
        const result = await quizCollectionsService.publishQuizCollection(id);
        if (result.validation && !result.validation.isValid) {
          Modal.warning({
            title: t('quiz.publishFailed', 'Cannot Publish'),
            content: (
              <div>
                <p>{t('quiz.publishErrors', 'Please fix these issues:')}</p>
                <ul>
                  {result.validation.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            ),
          });
          return;
        }
        setCollection(prev => ({ ...prev, isPublished: true }));
        message.success(t('quiz.publishSuccess', 'Quiz collection published'));
      }
    } catch (error) {
      message.error(t('quiz.togglePublishError', 'Failed to update status'));
    } finally {
      setPublishing(false);
    }
  };

  // Question drag end
  const handleQuestionDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = questions.findIndex(q => q.id === active.id);
    const newIndex = questions.findIndex(q => q.id === over.id);
    const newOrder = arrayMove(questions, oldIndex, newIndex);
    
    setQuestions(newOrder);
    
    try {
      await quizCollectionsService.reorderQuestions(id, newOrder.map(q => q.id));
    } catch (error) {
      message.error(t('quiz.reorderError', 'Failed to reorder questions'));
      fetchCollection();
    }
  };

  // ========== TOPIC CRUD ==========
  const handleAddTopic = () => {
    setEditingTopic(null);
    topicForm.resetFields();
    setTopicModalVisible(true);
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
    topicForm.setFieldsValue({ name: topic.name });
    setTopicModalVisible(true);
  };

  const handleTopicModalOk = async () => {
    try {
      const values = await topicForm.validateFields();
      
      if (editingTopic) {
        await quizCollectionsService.updateTopic(editingTopic.id, values);
        setTopics(prev => prev.map(t => t.id === editingTopic.id ? { ...t, ...values } : t));
        message.success(t('quiz.topicUpdated', 'Topic updated'));
      } else {
        const newTopic = await quizCollectionsService.addTopic(id, values);
        setTopics(prev => [...prev, newTopic]);
        message.success(t('quiz.topicAdded', 'Topic added'));
      }
      
      setTopicModalVisible(false);
    } catch (error) {
      if (error.errorFields) return;
      message.error(t('quiz.topicSaveError', 'Failed to save topic'));
    }
  };

  const handleDeleteTopic = async (topicId) => {
    try {
      await quizCollectionsService.deleteTopic(topicId);
      setTopics(prev => prev.filter(t => t.id !== topicId));
      // Also remove weights referencing the deleted topic from all questions
      setQuestions(prev => prev.map(q => ({
        ...q,
        weights: q.weights?.filter(w => w.topicId !== topicId) || []
      })));
      message.success(t('quiz.topicDeleted', 'Topic deleted'));
    } catch (error) {
      message.error(t('quiz.topicDeleteError', 'Failed to delete topic'));
    }
  };

  // ========== QUESTION WEIGHTS ==========
  const handleManageWeights = (question) => {
    setWeightsQuestion(question);
    setWeightsModalVisible(true);
  };

  const handleSaveWeights = async (questionId, weightsArray) => {
    try {
      setSavingWeights(true);
      await quizCollectionsService.setQuestionWeights(questionId, weightsArray);
      
      // Update local state with new weights
      setQuestions(prev => prev.map(q => {
        if (q.id === questionId) {
          return {
            ...q,
            weights: weightsArray.map(w => ({
              topicId: w.topicId,
              weight: w.weight,
              topicName: topics.find(t => t.id === w.topicId)?.name || ''
            }))
          };
        }
        return q;
      }));
      
      message.success(t('quiz.weights.saveSuccess', 'Weights saved successfully'));
      setWeightsModalVisible(false);
      setWeightsQuestion(null);
    } catch (error) {
      message.error(t('quiz.weights.saveError', 'Failed to save weights'));
    } finally {
      setSavingWeights(false);
    }
  };

  // ========== QUESTION CRUD ==========
  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setCurrentQuestionChoices([]);
    setQuestionImage(null);
    setQuestionPdf(null);
    setClearQuestionImage(false);
    setClearQuestionPdf(false);
    questionForm.resetFields();
    questionForm.setFieldsValue({
      code: `Q${questions.length + 1}`,
      isPublished: false,
    });
    setQuestionModalVisible(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setCurrentQuestionChoices(question.choices || []);
    setQuestionImage(null);
    setQuestionPdf(null);
    setClearQuestionImage(false);
    setClearQuestionPdf(false);
    questionForm.setFieldsValue({
      code: question.code,
      body: question.body,
      isPublished: question.isPublished,
      levelOfDifficultyId: question.levelOfDifficultyId,
      topic: question.topic,
      subtopic: question.subtopic,
      law: question.law,
      version: question.version,
    });
    setQuestionModalVisible(true);
  };

  const handleQuestionModalOk = async () => {
    try {
      const values = await questionForm.validateFields();
      setSaving(true);

      const choices = currentQuestionChoices.map((c, idx) => ({
        text: c.text,
        isCorrect: c.isCorrect,
        imageFileId: c.imageFileId,
      }));

      if (editingQuestion) {
        // Build update payload with clear flags for nullable fields
        // Only set clear flags when clearing without replacement
        const updatePayload = {
          ...values,
          clearImage: clearQuestionImage && !questionImage,
          clearPdf: clearQuestionPdf && !questionPdf,
          // Send clear flags when field is being set to null/empty
          clearLevelOfDifficulty: !values.levelOfDifficultyId && editingQuestion.levelOfDifficultyId ? true : undefined,
          clearTopic: (!values.topic || values.topic.trim() === '') && editingQuestion.topic ? true : undefined,
          clearSubtopic: (!values.subtopic || values.subtopic.trim() === '') && editingQuestion.subtopic ? true : undefined,
          clearLaw: (!values.law || values.law.trim() === '') && editingQuestion.law ? true : undefined,
          clearVersion: (!values.version || values.version.trim() === '') && editingQuestion.version ? true : undefined,
        };

        const result = await quizCollectionsService.updateQuestion(
          editingQuestion.id,
          updatePayload,
          questionImage,
          questionPdf
        );
        
        // Check if publish was rejected (validation failed when trying to publish)
        if (result.validation?.questionPublishWasRejected) {
          // Update local state with saved changes (question stays as draft)
          const updated = result.question || { ...editingQuestion, ...values, isPublished: false };
          setQuestions(prev => prev.map(q => q.id === editingQuestion.id ? updated : q));
          setEditingQuestion(updated);
          
          // Reset publish status in form to reflect reality
          questionForm.setFieldsValue({ isPublished: false });
          
          handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
          // Don't close modal so user can fix issues and try again
          setSaving(false);
          return;
        }
        
        // Update local state - use API response which reflects auto-unpublish if it happened
        const updated = result.question || { ...editingQuestion, ...values };
        setQuestions(prev => prev.map(q => q.id === editingQuestion.id ? updated : q));
        
        // Handle validation results (auto-unpublish warnings) from backend
        if (result.validation) {
          handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
        }
        message.success(t('quiz.questionUpdated', 'Question updated'));
      } else {
        const result = await quizCollectionsService.addQuestion(
          id,
          { ...values, choices },
          questionImage,
          questionPdf
        );
        
        const newQuestion = result.question;
        setQuestions(prev => [...prev, newQuestion]);
        
        if (result.validation) {
          handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
        }
        message.success(t('quiz.questionAdded', 'Question added'));
      }

      setQuestionModalVisible(false);
    } catch (error) {
      if (error.errorFields) return;
      console.error(error);
      message.error(t('quiz.questionSaveError', 'Failed to save question'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      const result = await quizCollectionsService.deleteQuestion(questionId);
      setQuestions(prev => prev.filter(q => q.id !== questionId));
      
      if (result.validation) {
        handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
      }
      message.success(t('quiz.questionDeleted', 'Question deleted'));
    } catch (error) {
      message.error(t('quiz.questionDeleteError', 'Failed to delete question'));
    }
  };

  const handleToggleQuestionPublish = async (question) => {
    try {
      const newIsPublished = !question.isPublished;
      const result = await quizCollectionsService.updateQuestion(question.id, {
        isPublished: newIsPublished,
      });
      
      // Check if publish was rejected (validation failed when trying to publish)
      if (result.validation?.questionPublishWasRejected) {
        handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
        // Don't update state - question stays as draft
        return;
      }
      
      // Use API response to update state (reflects actual server state)
      const updated = result.question || { ...question, isPublished: newIsPublished };
      setQuestions(prev => prev.map(q => 
        q.id === question.id ? updated : q
      ));
      
      if (result.validation) {
        handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
      }
      
      message.success(
        updated.isPublished 
          ? t('quiz.questionPublished', 'Question published') 
          : t('quiz.questionUnpublished', 'Question unpublished')
      );
    } catch (error) {
      message.error(t('quiz.questionPublishError', 'Failed to update question publish status'));
    }
  };

  // ========== CHOICE CRUD (for editing existing questions) ==========
  const handleAddChoice = () => {
    setEditingChoice(null);
    setChoiceImage(null);
    setClearChoiceImage(false);
    choiceForm.resetFields();
    choiceForm.setFieldsValue({ isCorrect: false });
    setChoiceModalVisible(true);
  };

  const handleEditChoice = (choice) => {
    setEditingChoice(choice);
    setChoiceImage(null);
    setClearChoiceImage(false);
    choiceForm.setFieldsValue({
      text: choice.text,
      isCorrect: choice.isCorrect,
    });
    setChoiceModalVisible(true);
  };

  const handleChoiceModalOk = async () => {
    try {
      const values = await choiceForm.validateFields();

      if (editingQuestion) {
        // We're editing an existing question - use API
        let result;
        if (editingChoice) {
          // Only set clearImage if we're clearing without replacement
          const shouldClearImage = clearChoiceImage && !choiceImage;
          result = await quizCollectionsService.updateChoice(
            editingChoice.id,
            { ...values, clearImage: shouldClearImage },
            choiceImage
          );
          const updated = result.choice || { ...editingChoice, ...values };
          setCurrentQuestionChoices(prev => 
            prev.map(c => c.id === editingChoice.id ? updated : c)
          );
        } else {
          result = await quizCollectionsService.addChoice(
            editingQuestion.id,
            values,
            choiceImage
          );
          setCurrentQuestionChoices(prev => [...prev, result.choice]);
        }
        
        // Handle validation results from backend (auto-unpublish warnings)
        if (result.validation) {
          handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
          
          // If question was auto-unpublished, update local state
          if (result.validation.questionWasAutoUnpublished) {
            setEditingQuestion(prev => ({ ...prev, isPublished: false }));
          }
        }
        
        // Refresh the question to get updated data
        const updatedCollection = await quizCollectionsService.getQuizCollection(id);
        const updatedQuestion = updatedCollection.questions.find(q => q.id === editingQuestion.id);
        if (updatedQuestion) {
          setCurrentQuestionChoices(updatedQuestion.choices || []);
          setQuestions(prev => prev.map(q => q.id === editingQuestion.id ? updatedQuestion : q));
          setEditingQuestion(updatedQuestion);
        }
      } else {
        // Creating a new question - just update local state
        const tempChoice = {
          id: `temp-${Date.now()}`,
          ...values,
        };
        setCurrentQuestionChoices(prev => [...prev, tempChoice]);
      }

      message.success(editingChoice ? t('quiz.choiceUpdated', 'Choice updated') : t('quiz.choiceAdded', 'Choice added'));
      setChoiceModalVisible(false);
    } catch (error) {
      if (error.errorFields) return;
      message.error(t('quiz.choiceSaveError', 'Failed to save choice'));
    }
  };

  const handleDeleteChoice = async (choiceId) => {
    if (editingQuestion && !String(choiceId).startsWith('temp-')) {
      try {
        const result = await quizCollectionsService.deleteChoice(choiceId);
        
        // Handle validation results from backend (auto-unpublish warnings)
        if (result.validation) {
          handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
          
          // If question was auto-unpublished, update local state
          if (result.validation.questionWasAutoUnpublished) {
            setEditingQuestion(prev => ({ ...prev, isPublished: false }));
            setQuestions(prev => prev.map(q => 
              q.id === editingQuestion.id ? { ...q, isPublished: false } : q
            ));
          }
        }
      } catch (error) {
        message.error(t('quiz.choiceDeleteError', 'Failed to delete choice'));
        return;
      }
    }
    setCurrentQuestionChoices(prev => prev.filter(c => c.id !== choiceId));
    message.success(t('quiz.choiceDeleted', 'Choice deleted'));
  };

  const handleToggleChoiceCorrect = async (choice) => {
    const newIsCorrect = !choice.isCorrect;
    
    if (editingQuestion && !String(choice.id).startsWith('temp-')) {
      try {
        const result = await quizCollectionsService.updateChoice(choice.id, { isCorrect: newIsCorrect });
        
        // Handle validation results from backend (auto-unpublish warnings)
        if (result.validation) {
          handleAutoUnpublishResult(result.validation, setCollection, setQuestions, t);
          
          // If question was auto-unpublished, update local state
          if (result.validation.questionWasAutoUnpublished) {
            setEditingQuestion(prev => ({ ...prev, isPublished: false }));
            setQuestions(prev => prev.map(q => 
              q.id === editingQuestion.id ? { ...q, isPublished: false } : q
            ));
          }
        }
      } catch (error) {
        message.error(t('quiz.choiceUpdateError', 'Failed to update choice'));
        return;
      }
    }
    
    setCurrentQuestionChoices(prev =>
      prev.map(c => c.id === choice.id ? { ...c, isCorrect: newIsCorrect } : c)
    );
  };

  const handleChoiceDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = currentQuestionChoices.findIndex(c => c.id === active.id);
    const newIndex = currentQuestionChoices.findIndex(c => c.id === over.id);
    const newOrder = arrayMove(currentQuestionChoices, oldIndex, newIndex);
    
    setCurrentQuestionChoices(newOrder);
    
    if (editingQuestion) {
      try {
        await quizCollectionsService.reorderChoices(
          editingQuestion.id,
          newOrder.map(c => c.id).filter(id => !String(id).startsWith('temp-'))
        );
      } catch (error) {
        // Silent fail for reorder
      }
    }
  };

  if (loading) {
    return (
      <PagesWrapper>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.editorContainer}>
        {/* Header - matching SurveyEditor style */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/quiz-collections')}>
              {t('common.back', 'Back')}
            </Button>
            <h1>{collection?.name || t('quiz.untitled', 'Untitled Collection')}</h1>
            <Tag color={collection?.isPublished ? 'green' : 'default'}>
              {collection?.isPublished ? t('quiz.published', 'Published') : t('quiz.draft', 'Draft')}
            </Tag>
            {collection?.addedByName && (
              <Tooltip title={t('quiz.createdBy', 'Created by')}>
                <Space size="small" className={styles.createdBy}>
                  <Avatar 
                    size="small" 
                    src={getFileUrl(collection.addedByProfileImageUrl)}
                    icon={!collection.addedByProfileImageUrl && <UserOutlined />}
                  />
                  <Text type="secondary">{collection.addedByName}</Text>
                </Space>
              </Tooltip>
            )}
          </div>
          <Space className={styles.headerActions}>
            {collection?.isPublished && (
              <Tooltip title={t('quiz.playQuiz', 'Play Quiz')}>
                <Button
                  icon={<PlayCircleOutlined />}
                  onClick={() => window.open(`/quiz/${id}`, '_blank')}
                >
                  {t('quiz.playQuiz', 'Play Quiz')}
                </Button>
              </Tooltip>
            )}
            <Tooltip title={t('quiz.statistics.title', 'Statistics')}>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => navigate(`/quiz-collections/${id}/statistics`)}
              >
                {t('quiz.statistics.title', 'Statistics')}
              </Button>
            </Tooltip>
            <Tooltip title={t('quiz.attempts.title', 'Attempts')}>
              <Button
                icon={<UnorderedListOutlined />}
                onClick={() => navigate(`/quiz-collections/${id}/attempts`)}
              >
                {t('quiz.attempts.title', 'Attempts')}
              </Button>
            </Tooltip>
            {canManageOwnership && (
              <Tooltip title={t('ownership.share', 'Share')}>
                <Button
                  icon={<TeamOutlined />}
                  onClick={() => setOwnershipModalVisible(true)}
                >
                  {t('ownership.share', 'Share')}
                </Button>
              </Tooltip>
            )}
            <Button
              icon={collection?.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={handleTogglePublish}
              loading={publishing}
            >
              {collection?.isPublished ? t('quiz.unpublish', 'Unpublish') : t('quiz.publish', 'Publish')}
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveDetails} loading={saving}>
              {t('common.save', 'Save')}
            </Button>
          </Space>
        </div>

        {/* Access Banner for shared/admin users */}
        {ownershipInfo && (
          <AccessBanner
            accessLevel={ownershipInfo.accessLevel}
            ownerInfo={ownershipInfo.mainOwner}
            canEdit={ownershipInfo.canEdit}
            canDelete={ownershipInfo.canDelete}
          />
        )}

        {/* Collection Name Edit */}
        <div className={styles.section}>
          <Form form={detailsForm} layout="vertical">
            <Form.Item
              name="name"
              label={t('quiz.collectionName', 'Collection Name')}
              rules={[{ required: true, message: t('quiz.nameRequired', 'Required') }]}
            >
              <Input disabled={!canEdit} />
            </Form.Item>
          </Form>
        </div>

        {/* Topics Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title level={4} className={styles.sectionTitle}>
              <TagsOutlined /> {t('quiz.topics', 'Topics')}
            </Title>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTopic}>
                {t('quiz.addTopic', 'Add Topic')}
              </Button>
            )}
          </div>
          <div className={styles.topicsContainer}>
            {topics.length === 0 ? (
              <Text type="secondary">{t('quiz.noTopics', 'No topics defined yet')}</Text>
            ) : (
              topics.map(topic => (
                <div key={topic.id} className={styles.topicTag}>
                  <span className={styles.topicTagText}>{topic.name}</span>
                  {canEdit && (
                    <Space size={4} className={styles.topicTagActions}>
                      <Tooltip title={t('quiz.editTopic', 'Edit')}>
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditTopic(topic)} />
                      </Tooltip>
                      <Popconfirm
                        title={t('quiz.deleteTopicConfirm', 'Delete this topic?')}
                        onConfirm={() => handleDeleteTopic(topic.id)}
                      >
                        <Tooltip title={t('quiz.deleteTopic', 'Delete')}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                      </Popconfirm>
                    </Space>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Questions Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title level={4} className={styles.sectionTitle}>
              <QuestionCircleOutlined /> {t('quiz.questions', 'Questions')} ({questions.length})
            </Title>
            {canEdit && (
              <Space>
                <Button icon={<ImportOutlined />} onClick={() => setBulkImportModalVisible(true)}>
                  {t('quiz.bulkImport.button', 'Bulk Import')}
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddQuestion}>
                  {t('quiz.addQuestion', 'Add Question')}
                </Button>
              </Space>
            )}
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleQuestionDragEnd}
          >
            <SortableContext
              items={questions.map(q => q.id)}
              strategy={rectSortingStrategy}
            >
              <div className={styles.questionsGrid}>
                {questions.map((question) => (
                  <SortableQuestionCard
                    key={question.id}
                    question={question}
                    onEdit={handleEditQuestion}
                    onDelete={handleDeleteQuestion}
                    onTogglePublish={handleToggleQuestionPublish}
                    onManageWeights={handleManageWeights}
                    onOpenAnalytics={(q) => {
                      setAnalyticsQuestion(q);
                      setAnalyticsModalOpen(true);
                    }}
                    stats={questionStatsMap[question.id]}
                    topicsCount={topics.length}
                  />
                ))}
                {canEdit && (
                  <div className={styles.addQuestionCard} onClick={handleAddQuestion}>
                    <PlusOutlined className={styles.addQuestionIcon} />
                    <span className={styles.addQuestionText}>{t('quiz.addQuestion', 'Add Question')}</span>
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        {/* Topic Modal */}
        <Modal
          title={editingTopic ? t('quiz.editTopic', 'Edit Topic') : t('quiz.addTopic', 'Add Topic')}
          open={topicModalVisible}
          onOk={handleTopicModalOk}
          onCancel={() => setTopicModalVisible(false)}
        >
          <Form form={topicForm} layout="vertical">
            <Form.Item
              name="name"
              label={t('quiz.topicName', 'Topic Name')}
              rules={[{ required: true, message: t('quiz.topicNameRequired', 'Required') }]}
            >
              <Input placeholder={t('quiz.topicNamePlaceholder', 'Enter topic name...')} />
            </Form.Item>
          </Form>
        </Modal>

        {/* Question Modal */}
        <Modal
          title={editingQuestion ? t('quiz.editQuestion', 'Edit Question') : t('quiz.addQuestion', 'Add Question')}
          open={questionModalVisible}
          onOk={handleQuestionModalOk}
          onCancel={() => setQuestionModalVisible(false)}
          width={900}
          confirmLoading={saving}
        >
          <div className={styles.questionForm}>
            {/* Basic Info Section */}
            <div className={`${styles.questionModalSection} ${styles.basicInfoSection}`}>
              <div className={styles.sectionLabel}>
                <QuestionCircleOutlined />
                {t('quiz.basicInfo', 'Basic Information')}
              </div>
              <Form form={questionForm} layout="vertical">
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Form.Item
                    name="code"
                    label={t('quiz.questionCode', 'Code')}
                    rules={[{ required: true, message: t('quiz.codeRequired', 'Required') }]}
                    style={{ width: 300, marginBottom: 0 }}
                  >
                    <Input placeholder="Q1" />
                  </Form.Item>
                  <Form.Item
                    name="isPublished"
                    label={t('quiz.publishStatus', 'Publish Status')}
                    style={{ marginBottom: 0 }}
                  >
                    <PublishButton />
                  </Form.Item>
                </div>
              </Form>
            </div>

            {/* Question Content Section */}
            <div className={`${styles.questionModalSection} ${styles.questionContentSection}`}>
              <div className={styles.sectionLabel}>
                <EditOutlined />
                {t('quiz.questionContent', 'Question Content')}
              </div>
              <Form form={questionForm} layout="vertical">
                <Form.Item
                  name="body"
                  label={t('quiz.questionBody', 'Question Text')}
                  rules={[{ required: true, message: t('quiz.bodyRequired', 'Required') }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea 
                    rows={4} 
                    placeholder={t('quiz.questionBodyPlaceholder', 'Enter your question...')} 
                    style={{ fontSize: 15 }}
                  />
                </Form.Item>
              </Form>
            </div>

            {/* Choices Section */}
            <div className={styles.choicesSection}>
              <div className={styles.choicesSectionHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                  <Text strong style={{ fontSize: 15 }}>{t('quiz.choices', 'Answer Choices')}</Text>
                </div>
                <Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleAddChoice}>
                  {t('quiz.addChoice', 'Add Choice')}
                </Button>
              </div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                {t('quiz.choicesHint', 'Check the box to mark correct answer(s). Multiple correct = multi-select question.')}
              </Text>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleChoiceDragEnd}
              >
                <SortableContext
                  items={currentQuestionChoices.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={styles.choicesList}>
                    {currentQuestionChoices.map((choice, index) => (
                      <SortableChoiceItem
                        key={choice.id}
                        choice={choice}
                        index={index}
                        onEdit={handleEditChoice}
                        onDelete={handleDeleteChoice}
                        onToggleCorrect={handleToggleChoiceCorrect}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {currentQuestionChoices.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: 32, 
                  background: '#fff', 
                  borderRadius: 8,
                  border: '2px dashed #d9d9d9'
                }}>
                  <QuestionCircleOutlined style={{ fontSize: 32, color: '#bfbfbf', marginBottom: 8 }} />
                  <Text type="secondary" style={{ display: 'block' }}>
                    {t('quiz.noChoices', 'No choices yet. Add at least 2 choices.')}
                  </Text>
                </div>
              )}
            </div>

            {/* Metadata Section */}
            <div className={`${styles.questionModalSection} ${styles.metadataSection}`}>
              <div className={styles.sectionLabel}>
                <TagsOutlined />
                {t('quiz.metadata', 'Categorization & Details')}
              </div>
              <Form form={questionForm} layout="vertical">
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <Form.Item
                    name="levelOfDifficultyId"
                    label={t('quiz.difficulty', 'Difficulty')}
                    style={{ width: 180, marginBottom: 12 }}
                  >
                    <Select
                      allowClear
                      placeholder={t('quiz.selectDifficulty', 'Select...')}
                      options={levelsOfDifficulty.map(l => ({ value: l.id, label: l.name }))}
                    />
                  </Form.Item>
                  <Form.Item 
                    name="topic" 
                    label={t('quiz.topicText', 'Topic')} 
                    style={{ width: 180, marginBottom: 12 }}
                  >
                    <Input placeholder={t('quiz.topicPlaceholder', 'e.g. Mathematics')} />
                  </Form.Item>
                  <Form.Item 
                    name="subtopic" 
                    label={t('quiz.subtopic', 'Subtopic')} 
                    style={{ width: 180, marginBottom: 12 }}
                  >
                    <Input placeholder={t('quiz.subtopicPlaceholder', 'e.g. Algebra')} />
                  </Form.Item>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <Form.Item 
                    name="law" 
                    label={t('quiz.law', 'Law/Reference')} 
                    style={{ flex: 1, minWidth: 200, marginBottom: 0 }}
                  >
                    <Input placeholder={t('quiz.lawPlaceholder', 'e.g. §12 AbcG')} />
                  </Form.Item>
                  <Form.Item 
                    name="version" 
                    label={t('quiz.version', 'Version')} 
                    style={{ width: 120, marginBottom: 0 }}
                  >
                    <Input placeholder="v1.0" />
                  </Form.Item>
                </div>
              </Form>
            </div>

            {/* Attachments Section */}
            <div className={`${styles.questionModalSection} ${styles.attachmentsSection}`}>
              <div className={styles.sectionLabel}>
                <FileImageOutlined />
                {t('quiz.attachments', 'Attachments')}
              </div>
              
              <div className={styles.attachmentsRow}>
                {/* Image Upload Area */}
                <div className={styles.imageUploadArea}>
                  <div className={styles.imageUploadLabel}>
                    <FileImageOutlined />
                    {t('quiz.questionImage', 'Question Image')}
                  </div>
                  <ImageDropZone
                    value={questionImage}
                    onChange={setQuestionImage}
                    existingUrl={editingQuestion?.imageUrl && !clearQuestionImage ? getFileUrl(editingQuestion.imageUrl) : null}
                    onClearExisting={() => setClearQuestionImage(true)}
                    compact
                  />
                </div>

                {/* PDF Upload Area */}
                <div className={styles.pdfUploadArea}>
                  <div className={styles.pdfUploadLabel}>
                    <FilePdfOutlined />
                    {t('quiz.questionPdf', 'PDF Attachment')}
                  </div>
                  
                  {/* Show existing PDF */}
                  {editingQuestion?.pdfUrl && !clearQuestionPdf && !questionPdf && (
                    <div className={`${styles.pdfDropZone} ${styles.hasFile}`}>
                      <div className={styles.pdfDropContent}>
                        <FilePdfOutlined className={styles.pdfDropIcon} />
                        <div className={styles.pdfFileName}>
                          <a 
                            href={getFileUrl(editingQuestion.pdfUrl)} 
                            target="_blank" 
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {editingQuestion.pdfFileName || 'PDF'}
                          </a>
                        </div>
                        <Button 
                          size="small" 
                          danger
                          onClick={() => setClearQuestionPdf(true)}
                        >
                          {t('common.remove', 'Remove')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Show newly selected PDF */}
                  {questionPdf && (
                    <div className={`${styles.pdfDropZone} ${styles.hasFile}`}>
                      <div className={styles.pdfDropContent}>
                        <FilePdfOutlined className={styles.pdfDropIcon} />
                        <div className={styles.pdfFileName}>
                          {questionPdf.name}
                        </div>
                        <Button 
                          size="small" 
                          danger
                          onClick={() => setQuestionPdf(null)}
                        >
                          {t('common.remove', 'Remove')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* PDF Upload dropzone - show when no PDF is present */}
                  {!questionPdf && !(editingQuestion?.pdfUrl && !clearQuestionPdf) && (
                    <Upload
                      accept=".pdf"
                      beforeUpload={(file) => {
                        setQuestionPdf(file);
                        setClearQuestionPdf(false);
                        return false;
                      }}
                      showUploadList={false}
                    >
                      <div className={styles.pdfDropZone}>
                        <div className={styles.pdfDropContent}>
                          <FilePdfOutlined className={styles.pdfDropIcon} />
                          <Text>{t('quiz.clickToUploadPdf', 'Click to upload PDF')}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('quiz.pdfFormat', 'PDF format only')}
                          </Text>
                        </div>
                      </div>
                    </Upload>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {/* Choice Modal */}
        <Modal
          title={editingChoice ? t('quiz.editChoice', 'Edit Choice') : t('quiz.addChoice', 'Add Choice')}
          open={choiceModalVisible}
          onOk={handleChoiceModalOk}
          onCancel={() => setChoiceModalVisible(false)}
          width={520}
        >
          <div className={styles.choiceModalContent}>
            {/* Choice Text Section */}
            <div className={styles.choiceModalSection}>
              <div className={styles.choiceModalLabel}>
                <EditOutlined />
                {t('quiz.choiceText', 'Choice Text')}
              </div>
              <Form form={choiceForm} layout="vertical">
                <Form.Item
                  name="text"
                  style={{ marginBottom: 0 }}
                >
                  <Input 
                    placeholder={t('quiz.choiceTextPlaceholder', 'Enter choice text...')} 
                    size="large"
                  />
                </Form.Item>
              </Form>
            </div>

            {/* Correct Answer Toggle Section */}
            <div className={styles.choiceModalSection}>
              <div className={styles.choiceModalLabel}>
                <CheckCircleOutlined />
                {t('quiz.answerStatus', 'Answer Status')}
              </div>
              <Form form={choiceForm}>
                <Form.Item
                  name="isCorrect"
                  valuePropName="value"
                  style={{ marginBottom: 0 }}
                >
                  <CorrectAnswerToggle />
                </Form.Item>
              </Form>
            </div>

            {/* Choice Image Section */}
            <div className={styles.choiceModalSection}>
              <div className={styles.choiceModalLabel}>
                <FileImageOutlined />
                {t('quiz.choiceImage', 'Choice Image')}
              </div>
              <ImageDropZone
                value={choiceImage}
                onChange={setChoiceImage}
                existingUrl={editingChoice?.imageUrl && !clearChoiceImage ? getFileUrl(editingChoice.imageUrl) : null}
                onClearExisting={() => setClearChoiceImage(true)}
                compact
              />
            </div>
          </div>
        </Modal>

        {/* Ownership Modal */}
        <OwnershipManager
          open={ownershipModalVisible}
          onClose={() => setOwnershipModalVisible(false)}
          ownershipInfo={ownershipInfo}
          onShare={shareWith}
          onRevoke={revoke}
          onGetAvailableUsers={getAvailableUsers}
          availableUsers={availableUsers}
          loading={ownershipLoading}
          loadingAvailableUsers={loadingAvailableUsers}
          canManageOwnership={canManageOwnership}
        />

        {/* Weights Modal */}
        <WeightsModal
          open={weightsModalVisible}
          question={weightsQuestion}
          topics={topics}
          onClose={() => {
            setWeightsModalVisible(false);
            setWeightsQuestion(null);
          }}
          onSave={handleSaveWeights}
          saving={savingWeights}
        />

        {/* Bulk Import Modal */}
        <BulkImportModal
          open={bulkImportModalVisible}
          collectionId={parseInt(id)}
          existingQuestionCodes={questions.map(q => q.code)}
          levelsOfDifficulty={levelsOfDifficulty}
          topics={topics}
          onClose={() => setBulkImportModalVisible(false)}
          onSuccess={fetchCollection}
        />

        {/* Question Analytics Modal */}
        <QuestionAnalyticsModal
          open={analyticsModalOpen}
          collectionId={parseInt(id)}
          question={analyticsQuestion}
          onClose={() => {
            setAnalyticsModalOpen(false);
            setAnalyticsQuestion(null);
          }}
        />
      </div>
    </PagesWrapper>
  );
};

export default QuizCollectionEditor;
